/**
 * Global SVG Cache Manager
 * Ensures SVGs are loaded only once and reused across all components
 */
import Vue from 'vue'

class SvgCacheManager {
  constructor() {
    this.cache = new Map()
    this.loadingPromises = new Map()
    // [PERF] Versión reactiva: cada vez que un SVG nuevo entra en cache, incrementamos
    // _version. Los componentes funcionales AppIcon leen _version.value en su render
    // function, lo que crea una dependencia reactiva: cuando carga un nuevo SVG, todos
    // los AppIcons que aún no lo tenían en cache se re-renderizan automáticamente.
    // Sin esto, los iconos no aparecían hasta que algo más disparaba un render del
    // componente padre (ej. pasar el ratón por encima).
    this._version = Vue.observable({ value: 0 })
  }

  /**
   * [PERF] Lectura reactiva de la versión del cache. Cualquier render que llame esto
   * se re-ejecutará cuando un SVG nuevo termine de cargar.
   */
  getVersion() {
    return this._version.value
  }

  /**
   * [PERF] Synchronous getter — returns cached SVG content immediately if present,
   * otherwise null. Used by the functional AppIcon to render without going through
   * an async path when the SVG is already cached (the common case in lists).
   */
  getSync(src, colorMap = null, preserveColors = false) {
    const cacheKey = this._generateCacheKey(src, colorMap, preserveColors)
    return this.cache.has(cacheKey) ? this.cache.get(cacheKey) : null
  }

  /**
   * [PERF] Fire-and-forget: kicks off the load if the SVG is not cached, but does
   * NOT block the caller. The functional AppIcon uses this to ensure the SVG is
   * eventually loaded for the next render cycle.
   */
  prime(src, colorMap = null, preserveColors = false, vuetify = null) {
    const cacheKey = this._generateCacheKey(src, colorMap, preserveColors)
    if (!this.cache.has(cacheKey) && !this.loadingPromises.has(cacheKey)) {
      // Fire and forget — caller doesn't await
      this.get(src, colorMap, preserveColors, vuetify).catch(() => {})
    }
  }

  /**
   * Get SVG content with optional color transformations
   * @param {string} src - SVG URL/path
   * @param {Object|null} colorMap - Color transformation map
   * @param {boolean} preserveColors - Whether to preserve original colors
   * @param {Object} vuetify - Vuetify instance for theme colors
   * @returns {Promise<string>} SVG content
   */
  async get(src, colorMap = null, preserveColors = false, vuetify = null) {
    const cacheKey = this._generateCacheKey(src, colorMap, preserveColors)

    // Return cached content if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)
    }

    // If already loading, return the existing promise
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)
    }

    // Start loading
    const loadingPromise = this._loadSvg(src, colorMap, preserveColors, vuetify)
      .then(content => {
        this.cache.set(cacheKey, content)
        this.loadingPromises.delete(cacheKey)
        // [PERF] Bump the reactive version so AppIcons that subscribed via
        // getVersion() re-render and pick up the freshly-cached SVG.
        this._version.value++
        return content
      })
      .catch(error => {
        this.loadingPromises.delete(cacheKey)
        throw error
      })

    this.loadingPromises.set(cacheKey, loadingPromise)
    return loadingPromise
  }

  /**
   * Load SVG from URL and apply transformations
   * @private
   */
  async _loadSvg(src, colorMap, preserveColors, vuetify) {
    try {
      const response = await fetch(src)

      if (!response.ok) {
        throw new Error(`Failed to load SVG: ${response.status} ${response.statusText}`)
      }

      let svg = await response.text()

      if (!preserveColors) {
        svg = this._applyColorTransformations(svg, colorMap, vuetify)
      }

      return svg
    } catch (error) {
      console.error('Error loading SVG:', error, 'src:', src)
      throw error
    }
  }

  /**
   * Apply color transformations to SVG content
   * @private
   */
  _applyColorTransformations(svg, colorMap, vuetify) {
    if (colorMap && typeof colorMap === 'object') {
      Object.entries(colorMap).forEach(([originalColor, themeColor]) => {
        // Get actual color from Vuetify theme or use themeColor directly
        const actualColor = (vuetify && vuetify.theme && vuetify.theme.themes &&
                           vuetify.theme.themes.light && vuetify.theme.themes.light[themeColor]) || themeColor
        const normalizedOriginal = originalColor.toUpperCase().replace('#', '')
        const fillRegex = new RegExp(`fill="#?${normalizedOriginal}"`, 'gi')
        const strokeRegex = new RegExp(`stroke="#?${normalizedOriginal}"`, 'gi')

        svg = svg.replace(fillRegex, `fill="${actualColor}"`)
        svg = svg.replace(strokeRegex, `stroke="${actualColor}"`)
      })
    } else {
      svg = svg.replace(/fill="(?!none)[^"]*"/g, 'fill="currentColor"')
      svg = svg.replace(/stroke="(?!none)[^"]*"/g, 'stroke="currentColor"')
    }

    return svg
  }

  /**
   * Generate unique cache key
   * @private
   */
  _generateCacheKey(src, colorMap, preserveColors) {
    return colorMap
      ? `${src}_${JSON.stringify(colorMap)}_${preserveColors}`
      : `${src}_${preserveColors}`
  }

  /**
   * Clear cache (useful for testing or memory management)
   */
  clear() {
    this.cache.clear()
    this.loadingPromises.clear()
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cachedItems: this.cache.size,
      pendingLoads: this.loadingPromises.size
    }
  }
}

// Export singleton instance
export default new SvgCacheManager()
